import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';

function DropzoneTile({ onAdd, isEmpty }) {
  const onDrop = useCallback(
    accepted => {
      if (accepted.length > 0) onAdd(accepted);
    },
    [onAdd]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'image/jpeg': ['.jpg', '.jpeg'] },
    onDrop,
    noClick: false,
  });

  return (
    <div
      {...getRootProps()}
      className={`enrollment-tile enrollment-tile-dropzone${isDragActive ? ' drag-active' : ''}`}
    >
      <input {...getInputProps()} />
      <svg
        width='24'
        height='24'
        viewBox='0 0 24 24'
        fill='none'
        aria-hidden='true'
      >
        <path
          d='M12 5v14M5 12h14'
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
        />
      </svg>
      <span>{isEmpty ? 'Upload a photo' : 'Upload another'}</span>
    </div>
  );
}

function PhotoTile({ src, enrolled, onRemove }) {
  return (
    <div className='enrollment-tile'>
      <img src={src} alt='' draggable={false} />
      {enrolled && (
        <div className='enrollment-tile-check' aria-label='Enrolled'>
          <svg width='10' height='10' viewBox='0 0 12 12' fill='none'>
            <path
              d='M2 6l3 3 5-5'
              stroke='#fff'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      )}
      <button
        type='button'
        className='enrollment-tile-remove'
        onClick={onRemove}
        aria-label='Remove photo'
      >
        ×
      </button>
    </div>
  );
}

export default function EnrollmentGallery({
  existingPhotos,
  newPhotos,
  removedIds,
  onAdd,
  onRemoveNew,
  onRemoveExisting,
  localDevWarning,
}) {
  // Stable object URLs for File previews — revoked when component unmounts
  const previewUrls = useMemo(
    () => newPhotos.map(f => URL.createObjectURL(f)),
    [newPhotos]
  );

  const visibleExisting = existingPhotos.filter(p => !removedIds.has(p.id));
  const totalVisible = visibleExisting.length + newPhotos.length;

  const toAdd = newPhotos.length;
  const toRemove = removedIds.size;
  const changeParts = [];
  if (toAdd > 0)
    changeParts.push(`${toAdd} photo${toAdd !== 1 ? 's' : ''} will be added`);
  if (toRemove > 0)
    changeParts.push(
      `${toRemove} photo${toRemove !== 1 ? 's' : ''} will be removed`
    );
  const changesSummary = changeParts.join(' · ');

  if (localDevWarning) {
    return (
      <div className='enrollment-local-warning'>
        Enrollment photo processing is not available in local development.
      </div>
    );
  }

  return (
    <div className='enrollment-gallery'>
      <label className='enrollment-gallery-label'>Artist Headshots</label>
      <p className='enrollment-gallery-hint'>
        Headshots are used to automatically identify and tag artists in
        production photos using AI.
      </p>
      <div className='enrollment-grid'>
        {visibleExisting.map(photo => (
          <PhotoTile
            key={photo.id}
            src={photo.thumbnailUrl}
            enrolled={!!photo.faceId}
            onRemove={() => onRemoveExisting(photo.id)}
          />
        ))}
        {newPhotos.map((_, i) => (
          <PhotoTile
            key={`new-${i}`}
            src={previewUrls[i]}
            enrolled={false}
            onRemove={() => onRemoveNew(i)}
          />
        ))}
        <DropzoneTile onAdd={onAdd} isEmpty={totalVisible === 0} />
      </div>
      {changesSummary && (
        <p className='enrollment-changes-summary'>{changesSummary}</p>
      )}
    </div>
  );
}
